// A dropped SKILL.md or skill folder → a draft with every problem named before anything is
// sent. Limits mirror server cyborg/skill-files.ts + the upsert schema; the relay re-checks
// them on cyborg:upsert_skill, so a drift only moves where a skill is refused, never whether.
import type { ImportedSkillFile } from "$lib/plugins/agents/types.js";

export const SKILL_CONTENT_MAX = 200_000;
export const SKILL_NAME_MAX = 120;
// agentskills.io caps a description at 1024; the relay accepts 2000.
export const SKILL_DESCRIPTION_MAX = 1024;
export const SKILL_LONG_LINES = 500;
export const SKILL_FILES_MAX_COUNT = 64;
export const SKILL_FILES_MAX_TOTAL_BYTES = 1024 * 1024;
export const SKILL_FILE_MAX_BYTES = 512 * 1024;
export const SKILL_FILE_MAX_DEPTH = 8;
const SKILL_FILE_PATH_MAX_LENGTH = 255;

export const SKILL_SLUG_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;
// skill-files.ts SEGMENT: plain names, no traversal, no control characters, no backslash.
const PATH_SEGMENT_RE = /^[A-Za-z0-9._@+-][A-Za-z0-9._@+ -]*$/;

// The one sentence every Skills surface uses to say what a skill is.
export const SKILL_EXPLAINER =
  "A skill is a playbook, written as a SKILL.md file, that your agents open when a task calls for it.";

// ─── Names ───────────────────────────────────────────────────────────────────

const ACRONYMS = new Set([
  "ai",
  "api",
  "aws",
  "cli",
  "css",
  "csv",
  "docx",
  "gcp",
  "html",
  "http",
  "ios",
  "json",
  "llm",
  "mcp",
  "md",
  "pdf",
  "pptx",
  "pr",
  "qa",
  "seo",
  "sql",
  "ui",
  "url",
  "ux",
  "xlsx",
  "yaml",
]);

/** "rodrigo-ideation-skill" → "Rodrigo ideation skill", "pdf" → "PDF"; real names pass through. */
export function humanizeSkillName(name: string): string {
  const trimmed = name.trim();
  if (!/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/.test(trimmed)) return trimmed;
  const words = trimmed.split(/[-_]/).map((w) => (ACRONYMS.has(w) ? w.toUpperCase() : w));
  return words.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(" ");
}

/** The ID an agent opens a skill by: "Release notes!" → "release-notes". */
export function slugifySkillName(text: string): string {
  const slug = text
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64)
    .replace(/-+$/, "");
  return slug || "skill";
}

/** `base`, or `base-2`, `base-3`… — the first one nobody in `taken` uses. */
export function uniqueSlug(base: string, taken: ReadonlySet<string>): string {
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) {
    const suffix = `-${n}`;
    const candidate = `${base.slice(0, 64 - suffix.length)}${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
}

// ─── SKILL.md front matter ───────────────────────────────────────────────────

export interface SkillMd {
  present: boolean;
  name?: string;
  description?: string;
  // Front-matter lines other than name/description (license, metadata…), kept verbatim.
  extra: string[];
  body: string;
}

const FRONT_MATTER_RE = /^﻿?---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/;
const TOP_KEY_RE = /^([A-Za-z0-9_-]+):(?:\s+(.*))?$/;

function unquote(value: string): string {
  const v = value.trim();
  if (v.length >= 2 && v.startsWith('"') && v.endsWith('"')) {
    try {
      const parsed: unknown = JSON.parse(v);
      if (typeof parsed === "string") return parsed;
    } catch {
      // intentional: not JSON-escaped (YAML allows escapes JSON does not), so the inner text is the value.
      return v.slice(1, -1);
    }
  }
  if (v.length >= 2 && v.startsWith("'") && v.endsWith("'"))
    return v.slice(1, -1).replace(/''/g, "'");
  return v;
}

/**
 * A SKILL.md split into its front matter and body. Reads the shapes real skills use,
 * including folded descriptions (`description: >` + indented lines) that a one-line
 * regex reads as empty.
 */
export function parseSkillMd(content: string): SkillMd {
  const m = FRONT_MATTER_RE.exec(content);
  if (!m) return { present: false, extra: [], body: content.replace(/^﻿/, "") };
  const lines = m[1].split(/\r?\n/);
  const out: SkillMd = {
    present: true,
    extra: [],
    body: content.slice(m[0].length).replace(/^\s*\n/, ""),
  };
  for (let i = 0; i < lines.length; i++) {
    const key = TOP_KEY_RE.exec(lines[i]);
    // Continuation lines of a key we keep travel with it.
    let end = i + 1;
    while (end < lines.length && (/^[ \t]/.test(lines[end]) || lines[end].trim() === "")) end++;
    const block = lines.slice(i, end);
    i = end - 1;
    if (!key || (key[1] !== "name" && key[1] !== "description")) {
      out.extra.push(...block);
      continue;
    }
    const head = (key[2] ?? "").trim();
    const rest = block
      .slice(1)
      .map((l) => l.trim())
      .filter(Boolean);
    const value =
      /^[>|][+-]?$/.test(head) || head === "" ? rest.join(" ") : unquote([head, ...rest].join(" "));
    if (key[1] === "name") out.name = value;
    else out.description = value;
  }
  // Trailing blank continuation lines are not content.
  while (out.extra.length > 0 && out.extra[out.extra.length - 1].trim() === "") out.extra.pop();
  return out;
}

function yamlScalar(value: string): string {
  const v = value.replace(/\s+/g, " ").trim();
  // A plain scalar unless YAML would read it as something else.
  if (/^[A-Za-z0-9(][^\n]*$/.test(v) && !/: | #|:$/.test(v) && !/["'`]/.test(v)) return v;
  return JSON.stringify(v);
}

/** The SKILL.md that gets saved: name + description up front, the other keys kept, then the body. */
export function composeSkillMd(input: {
  slug: string;
  description: string;
  extra?: readonly string[];
  body: string;
}): string {
  const head = [`name: ${input.slug}`];
  if (input.description.trim()) head.push(`description: ${yamlScalar(input.description)}`);
  head.push(...(input.extra ?? []));
  return `---\n${head.join("\n")}\n---\n\n${input.body.replace(/^\s*\n/, "").trimEnd()}\n`;
}

export const SKILL_TEMPLATE_BODY = `# What this skill does

One or two sentences on the job, and what "done well" looks like.

## Steps

1. **First** — what the agent does first, and why.
2. **Then** — the next step.
3. **Finish** — how it checks the result before replying.

## Rules

- Always …
- Never …
`;

// ─── Reading an upload ───────────────────────────────────────────────────────

/** One dropped or picked file. `read` is called only when the file is worth reading. */
export interface UploadInput {
  path: string;
  size: number;
  read: () => Promise<Uint8Array>;
}

export type CheckLevel = "ok" | "warn" | "error";

export interface UploadCheck {
  level: CheckLevel;
  text: string;
}

export interface UploadDraft {
  name: string;
  slug: string;
  description: string;
  extra: string[];
  body: string;
  files: ImportedSkillFile[];
}

export interface UploadReview {
  // What was dropped, in words: "SKILL.md" or "the pdf-tools folder".
  label: string;
  checks: UploadCheck[];
  ok: boolean;
  draft?: UploadDraft;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function pathError(path: string): string | undefined {
  if (path.length > SKILL_FILE_PATH_MAX_LENGTH) return "its path is too long";
  const segments = path.split("/");
  if (segments.length > SKILL_FILE_MAX_DEPTH)
    return `it is nested more than ${SKILL_FILE_MAX_DEPTH} folders deep`;
  if (segments.some((s) => s === "." || s === ".." || !PATH_SEGMENT_RE.test(s))) {
    return "its name has characters a skill file can't use";
  }
  return undefined;
}

function toBase64(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

const STRICT_UTF8 = new TextDecoder("utf-8", { fatal: true });

/** Text stays text; anything that isn't clean UTF-8 travels as base64. */
function encodeFile(bytes: Uint8Array): { content: string; encoding: "utf8" | "base64" } {
  try {
    const text = STRICT_UTF8.decode(bytes);
    if (!text.includes("\0")) return { content: text, encoding: "utf8" };
  } catch {
    // intentional: not valid UTF-8 means a binary resource, stored byte for byte as base64.
  }
  return { content: toBase64(bytes), encoding: "base64" };
}

const hidden = (path: string): boolean =>
  path.split("/").some((s) => s.startsWith(".") || s === "__MACOSX");

class UploadRefused extends Error {}

/** A file the checks refused (too big, wrong kind, a bad path), not one that failed to read. */
export function isUploadRefused(err: unknown): boolean {
  return err instanceof UploadRefused;
}

interface Located {
  folder: string;
  label: string;
  skillMd: UploadInput;
  others: UploadInput[];
  skipped: number;
}

// Which file is SKILL.md, and what else came with it. A folder arrives as "folder/…" paths.
function locate(inputs: readonly UploadInput[]): Located {
  if (inputs.length === 0)
    throw new UploadRefused("Nothing was dropped. Drop a SKILL.md file or a skill folder.");
  if (inputs.some((i) => /\.zip$/i.test(i.path))) {
    throw new UploadRefused(
      "ZIP files aren't supported yet. Unzip it and drop the folder, or drop just its SKILL.md.",
    );
  }
  const all = inputs.map((i) => Object.assign({}, i, { path: i.path.replace(/^\/+/, "") }));
  const firstSegments = new Set(all.map((i) => (i.path.includes("/") ? i.path.split("/")[0] : "")));
  const folder = firstSegments.size === 1 && !firstSegments.has("") ? [...firstSegments][0] : "";
  const rel = all.map((i) =>
    Object.assign(i, { path: folder ? i.path.slice(folder.length + 1) : i.path }),
  );
  const visible = rel.filter((i) => !hidden(i.path));
  let label = "your files";
  if (folder) label = `the ${folder} folder`;
  else if (visible.length === 1) label = visible[0].path;

  let skillMd = visible.find((i) => i.path.toLowerCase() === "skill.md");
  if (!skillMd && !folder && visible.length === 1 && /\.(md|markdown)$/i.test(visible[0].path)) {
    skillMd = visible[0];
  }
  if (!skillMd) {
    const loose = folder || visible.length > 1;
    throw new UploadRefused(
      loose
        ? "There's no SKILL.md at the top of the folder. Every skill needs one."
        : "That isn't a Markdown file. Drop a SKILL.md, or the folder that holds one.",
      { cause: label },
    );
  }
  const found = skillMd;
  return {
    folder,
    label,
    skillMd: found,
    others: visible.filter((i) => i !== found),
    skipped: rel.length - visible.length,
  };
}

async function readSkillMd(file: UploadInput): Promise<string> {
  const tooBig = `${file.path} is too large; a skill's instructions can be at most 200 KB.`;
  if (file.size > SKILL_CONTENT_MAX * 4) throw new UploadRefused(tooBig);
  const content = new TextDecoder().decode(await file.read());
  if (content.includes("\0")) throw new UploadRefused(`${file.path} isn't a text file.`);
  if (content.length > SKILL_CONTENT_MAX) throw new UploadRefused(tooBig);
  if (!content.trim()) throw new UploadRefused(`${file.path} is empty.`);
  return content;
}

// What the name will be, and the warnings that explain it.
function nameChecks(
  md: SkillMd,
  folder: string,
  fileStem: string,
): { declared: string; fallback: string; slug: string; checks: UploadCheck[] } {
  const declared = md.name?.trim() ?? "";
  const fallback = folder || (fileStem.toLowerCase() === "skill" ? "" : fileStem);
  const source = declared || fallback || "new-skill";
  const slug = SKILL_SLUG_RE.test(source) ? source : slugifySkillName(source);
  const checks: UploadCheck[] = [];
  const warn = (text: string): void => void checks.push({ level: "warn", text });
  if (!md.present)
    warn("No front matter (the --- block with a name and description). Fill them in below.");
  else if (!declared)
    warn(
      `No name in the front matter, so it's named after ${fallback ? `“${fallback}”` : "the file"}.`,
    );
  else if (declared !== slug)
    warn(`“${declared}” isn't a valid skill ID, so agents will open it as ${slug}.`);
  else if (folder && folder !== declared) {
    warn(
      `The folder is called “${folder}” but the skill calls itself “${declared}”. It's saved as ${declared}.`,
    );
  }
  const description = md.description ?? "";
  if (md.present && !description.trim())
    warn("No description. Add one below: it's how agents decide when to use the skill.");
  else if (description.length > SKILL_DESCRIPTION_MAX) {
    warn(
      `The description is ${description.length} characters; keep it under ${SKILL_DESCRIPTION_MAX}. Trim it below.`,
    );
  }
  return { declared, fallback, slug, checks };
}

// The folder's other files, as upsert_skill takes them, or the first reason one can't go.
async function bundle(
  others: readonly UploadInput[],
): Promise<{ files: ImportedSkillFile[]; total: number }> {
  if (others.length > SKILL_FILES_MAX_COUNT) {
    throw new UploadRefused(
      `The folder has ${others.length} files besides SKILL.md; a skill can bundle at most ${SKILL_FILES_MAX_COUNT}.`,
    );
  }
  const files: ImportedSkillFile[] = [];
  let total = 0;
  for (const f of others) {
    const why = pathError(f.path);
    if (why) throw new UploadRefused(`${f.path} can't be included: ${why}.`);
    if (f.size > SKILL_FILE_MAX_BYTES) {
      throw new UploadRefused(
        `${f.path} is ${formatBytes(f.size)}; each bundled file can be at most 512 KB.`,
      );
    }
    total += f.size;
    if (total > SKILL_FILES_MAX_TOTAL_BYTES) {
      throw new UploadRefused(
        "The bundled files add up to more than 1 MB, the most a skill can carry.",
      );
    }
    // ponytail: uploads never carry the executable bit; an admin who needs one sets it
    // through an import that declares it. Nothing runs on upload either way.
    files.push({ path: f.path, ...encodeFile(await f.read()), mode: "0644", size: f.size });
  }
  return { files, total };
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/**
 * Everything dropped, checked against what the relay accepts. Paths are relative
 * ("my-skill/SKILL.md" from a folder, "SKILL.md" or "notes.md" for a single file).
 */
export async function reviewUpload(inputs: readonly UploadInput[]): Promise<UploadReview> {
  const checks: UploadCheck[] = [];
  let label = "your file";
  try {
    const found = locate(inputs);
    label = found.label;
    const content = await readSkillMd(found.skillMd);
    const md = parseSkillMd(content);
    const lines = md.body.split("\n").length;
    checks.push({
      level: "ok",
      text: `Read ${found.skillMd.path} (${plural(lines, "line", "lines")})`,
    });
    const named = nameChecks(md, found.folder, found.skillMd.path.replace(/\.(md|markdown)$/i, ""));
    checks.push(...named.checks);
    if (lines > SKILL_LONG_LINES) {
      checks.push({
        level: "warn",
        text: `It's long (${lines} lines). Agents read all of it each time; under ${SKILL_LONG_LINES} lines works best.`,
      });
    }
    const { files, total } = await bundle(found.others);
    if (files.length > 0) {
      checks.push({
        level: "ok",
        text: `${plural(files.length, "bundled file", "bundled files")} (${formatBytes(total)}). Saved as plain files: nothing runs when you save.`,
      });
    }
    if (found.skipped > 0) {
      checks.push({
        level: "ok",
        text: `Skipped ${plural(found.skipped, "hidden file", "hidden files")} (like .DS_Store).`,
      });
    }
    return {
      label,
      checks,
      ok: true,
      draft: {
        name: humanizeSkillName(named.declared || named.fallback || "New skill"),
        slug: named.slug,
        description: (md.description ?? "").slice(0, SKILL_DESCRIPTION_MAX * 2),
        extra: md.extra,
        body: md.body,
        files,
      },
    };
  } catch (err) {
    if (!(err instanceof UploadRefused)) throw err;
    if (typeof err.cause === "string") label = err.cause;
    return { label, checks: [...checks, { level: "error", text: err.message }], ok: false };
  }
}

// ─── Browser glue: FileList / DataTransfer → UploadInput[] ───────────────────

function fromFile(file: File, path: string): UploadInput {
  return { path, size: file.size, read: async () => new Uint8Array(await file.arrayBuffer()) };
}

/** A picked file list; a folder pick carries `webkitRelativePath`. */
export function inputsFromFileList(list: FileList | readonly File[]): UploadInput[] {
  return [...list].map((f) => fromFile(f, f.webkitRelativePath || f.name));
}

function readAllEntries(dir: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  const reader = dir.createReader();
  const out: FileSystemEntry[] = [];
  return new Promise((resolve, reject) => {
    // readEntries answers in batches until it returns an empty one.
    const next = (): void =>
      reader.readEntries((batch) => {
        if (batch.length === 0) resolve(out);
        else {
          out.push(...batch);
          next();
        }
      }, reject);
    next();
  });
}

async function walk(entry: FileSystemEntry, into: UploadInput[]): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) =>
      (entry as FileSystemFileEntry).file(resolve, reject),
    );
    into.push(fromFile(file, entry.fullPath.replace(/^\/+/, "")));
  } else if (entry.isDirectory) {
    for (const child of await readAllEntries(entry as FileSystemDirectoryEntry))
      await walk(child, into);
  }
}

/** A drop: files as they are, folders walked (Chrome, Safari and Firefox all expose entries). */
export async function inputsFromDrop(data: DataTransfer): Promise<UploadInput[]> {
  // Entries must be taken synchronously, before the first await empties the DataTransfer.
  const entries: FileSystemEntry[] = [];
  for (const item of data.items) {
    if (item.kind !== "file") continue;
    const entry = item.webkitGetAsEntry?.();
    if (!entry) return inputsFromFileList(data.files);
    entries.push(entry);
  }
  if (entries.length === 0) return inputsFromFileList(data.files);
  const out: UploadInput[] = [];
  for (const entry of entries) await walk(entry, out);
  return out;
}

/**
 * Files added to a skill that keeps `kept`: each one checked like a dropped folder's, and the
 * limits counted with what stays. A file with a kept file's path replaces it.
 */
export async function bundleAddedFiles(
  inputs: readonly UploadInput[],
  kept: readonly { path: string; size: number }[],
): Promise<ImportedSkillFile[]> {
  const names = inputs
    .map((i) => ({ ...i, path: i.path.replace(/^\/+/, "") }))
    .filter((i) => !hidden(i.path));
  if (names.some((i) => i.path.toLowerCase() === "skill.md")) {
    throw new UploadRefused("SKILL.md is the skill's instructions; edit it in the form instead.");
  }
  const replaced = new Set(names.map((i) => i.path));
  const staying = kept.filter((f) => !replaced.has(f.path));
  const count = staying.length + names.length;
  if (count > SKILL_FILES_MAX_COUNT) {
    throw new UploadRefused(
      `A skill can bundle at most ${SKILL_FILES_MAX_COUNT} files; this would make ${count}.`,
    );
  }
  const { files, total } = await bundle(names);
  const all = total + staying.reduce((sum, f) => sum + f.size, 0);
  if (all > SKILL_FILES_MAX_TOTAL_BYTES) {
    throw new UploadRefused(
      "The bundled files would add up to more than 1 MB, the most a skill can carry.",
    );
  }
  return files;
}
export const SKILL_LABEL_MAX_LENGTH = 40;
export const SKILL_LABELS_MAX_COUNT = 20;

export function parseSkillLabels(text: string): { labels: string[]; error?: string } {
  const labels = text.trim() ? text.split(",").map((label) => label.trim().toLowerCase()) : [];
  if (
    labels.length > SKILL_LABELS_MAX_COUNT ||
    labels.some(
      (label) => !label || label.length > SKILL_LABEL_MAX_LENGTH || !/^[^\p{Cc},]+$/u.test(label),
    )
  ) {
    return {
      labels: [],
      error: `Use up to ${SKILL_LABELS_MAX_COUNT} labels, each 1–${SKILL_LABEL_MAX_LENGTH} characters, without control characters.`,
    };
  }
  return { labels: [...new Set(labels)].sort() };
}

export function normalizeSkillLabelFilter(text: string): string | undefined {
  return text.trim().toLowerCase() || undefined;
}
